import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { trackNightModeToggle } from '@/services/analytics';

// Clé de stockage dans le localStorage
const STORAGE_KEY = 'nightVisionEnabled';

interface NightVisionContextType {
    isNightVision: boolean;
    toggleNightVision: () => void;
}

const NightVisionContext = createContext<NightVisionContextType>({
    isNightVision: false,
    toggleNightVision: () => { },
});

/**
 * Hook pour accéder au mode Vision Nocturne.
 * Fournit l'état actuel et un toggle pour l'activer/désactiver.
 */
export const useNightVision = () => useContext(NightVisionContext);

/**
 * Provider du mode Vision Nocturne.
 * Ajoute/retire la classe CSS 'night-vision' sur <html> pour activer le filtre rouge global.
 * L'état est persisté dans le localStorage pour survivre aux redémarrages.
 */
export const NightVisionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Initialiser depuis le localStorage
    const [isNightVision, setIsNightVision] = useState(() => {
        try {
            return localStorage.getItem(STORAGE_KEY) === 'true';
        } catch {
            return false;
        }
    });

    // Appliquer/retirer la classe CSS sur <html>
    useEffect(() => {
        if (isNightVision) {
            document.documentElement.classList.add('night-vision');
        } else {
            document.documentElement.classList.remove('night-vision');
        }
    }, [isNightVision]);

    // Toggle avec sauvegarde dans le localStorage et Analytics
    const toggleNightVision = useCallback(() => {
        setIsNightVision(prev => {
            const newValue = !prev;
            try {
                localStorage.setItem(STORAGE_KEY, String(newValue));
            } catch {
                // Silently fail si localStorage n'est pas disponible
            }
            
            // Track analytics event
            trackNightModeToggle(newValue);
            
            return newValue;
        });
    }, []);

    return (
        <NightVisionContext.Provider value={{ isNightVision, toggleNightVision }}>
            {children}
        </NightVisionContext.Provider>
    );
};

